import React, { useMemo, useState } from "react"; // ⬅️ Added useMemo
import { Bar } from "@reactchartjs/react-chart.js";
import { Box, IconButton, Paper, Typography } from "@material-ui/core";
import { withStyles } from "@material-ui/styles";
import { compose } from "recompose";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos"; // For Prev button
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos"; // For Next button
// --- Color and V2 Annotation Logic (Remains the same) ---
const WEEKS_PER_PAGE = 7;
// ⬅️ V2 Custom Annotation: Draw labels after animation completes
const drawAnnotations = (chartInstance) => {
  const ctx = chartInstance.chart.ctx;
  const lineMeta = chartInstance.getDatasetMeta(1); // Metadata for the line dataset (index 1)

  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "bottom";
  ctx.fillStyle = "#4CAF50"; // Green text color
  ctx.font = "bold 12px Arial";

  // Access the data directly from the chart's config data
  const retentionData = chartInstance.config.data.datasets[1].data;


  // Iterate through each point on the line
  lineMeta.data.forEach((point, index) => {
    const dataValue = retentionData[index];
    // Use Math.round to display clean percentages
    const label = `${Math.round(dataValue)}%`;

    // Draw the text label 10 pixels above the data point
    ctx.fillText(label, point._model.x, point._model.y + 10);
  });
  ctx.restore();
};

const RetentionOptions = {
  responsive: true,
  maintainAspectRatio: false,
  legend: { display: false },
  scales: {
    xAxes: [{ gridLines: { display: false } }],
    yAxes: [
      {
        id: "y-axis-1",
        type: "linear",
        position: "left",
        ticks: { beginAtZero: true },
        gridLines: { color: "#e0e0e0" },
      },
    ],
  },
  tooltips: {
    enabled: true,
    // mode: "index",
    // intersect: false,
    filter: function (tooltipItem, data) {
      return tooltipItem.datasetIndex === 0; // Show tooltip only for bar dataset
    },
  },
  animation: {
    duration: 0,
     onComplete: drawAnnotations, 
  },
};

// --- 2. React Component with Data Mapping ---

// ⬅️ Accept the data prop
function RetentionGraphView({ classes, data }) {
  // State to track the current page (starting at index 0)
  const [currentPage, setCurrentPage] = useState(0);

  // Calculate total number of pages needed
  const totalWeeks = data ? data.length : 0;
  const totalPages = Math.ceil(totalWeeks / WEEKS_PER_PAGE);

  // 💡 Logic to slice the data array based on the current page
  const paginatedData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const start = currentPage * WEEKS_PER_PAGE;
    const end = start + WEEKS_PER_PAGE;
    return data.slice(start, end);
  }, [data, currentPage]); // Recalculate when data or page changes

  // 💡 Handler Functions for Navigation
  const handleNext = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  // --- Data Mapping to Chart.js Format (using paginatedData) ---
  const chartData = useMemo(() => {
    if (paginatedData.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Map the sliced data points
    const labels = paginatedData.map((item) => `Week ${item.week}`);
    const activeParticipants = paginatedData.map(
      (item) => item.activeParticipants
    );
    const retentionPercentages = paginatedData.map(
      (item) => item.retentionPercentage
    );

    return {
      labels: labels,
      datasets: [
         {
          type: "line",
          label: "Retention (%)",
          data: retentionPercentages,
          fill: false,
          borderColor: "#66BB6A",
          backgroundColor: "#66BB6A",
          pointRadius: 6,
          yAxisID: "y-axis-1",
        },
        {
          type: "bar",
          label: "Retention (%)",
          data: retentionPercentages,
          backgroundColor: "rgba(54, 162, 235, 1)",
          borderColor: "rgba(54, 162, 235, 1)",
          barPercentage: 0.8,
          categoryPercentage: 0.9,
          yAxisID: "y-axis-1",
        },
       
      ],
    };
  }, [paginatedData]);

  // 💡 FIX 2: Dynamic max Y-axis value
  // Find the max value in the bar data for setting the Y-axis max scale
  const maxYValue = useMemo(() => {
    if (chartData.datasets.length === 0) return 100; // Default max
    const maxBarValue = Math.max(...chartData.datasets[0].data);
    return Math.ceil(maxBarValue / 10) * 10; // Round up to nearest 10 for clean scale
  }, [chartData]);

  // 💡 FIX 3: Clone options and update max scale
  const dynamicOptions = useMemo(() => {
    const optionsClone = JSON.parse(JSON.stringify(RetentionOptions));
    if (optionsClone.scales.yAxes && optionsClone.scales.yAxes[0].ticks) {
      optionsClone.scales.yAxes[0].ticks.max = maxYValue;
    }
    return optionsClone;
  }, [maxYValue]);

  return (
    <>
      <IconButton
        onClick={handlePrevious}
        disabled={currentPage === 0}
        size="small"
      >
        <ArrowBackIosIcon style={{ fontSize: 16 }} />
      </IconButton>

      <Box className={classes.chartWrapper}>
        {chartData.datasets.length > 0 ? (
          <Bar
            data={chartData}
            options={dynamicOptions} // Use dynamic options
          />
        ) : (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <Typography color="textSecondary">
              No retention data available.
            </Typography>
          </Box>
        )}
      </Box>

      <IconButton
        onClick={handleNext}
        disabled={currentPage === totalPages - 1}
        size="small"
      >
        <ArrowForwardIosIcon style={{ fontSize: 16 }} />
      </IconButton>
    </>
  );
}

const styles = (theme) => ({
  chartWrapper: {
    flexGrow: 1,
    position: "relative",
    height: "300px",
  },
});

export default compose(withStyles(styles, { withTheme: true }))(
  RetentionGraphView
);

// Note: You must ensure this component is rendered by a parent component that
// passes the JSON data array as the 'data' prop.
